import React from 'react'
import { useSearchParams } from 'react-router-dom'
import Section from '../components/layout/Section'
import Seo from '../components/seo/Seo'
import { useBreadcrumbs } from '../seo/useBreadcrumbs'
import { trackEvent } from '../lib/analytics'

// Panorama CTAs deep-link here with ?subject= (Polish Plan item 11.5)
const SUBJECT_TO_INTEREST: Record<string, string> = {
  VALUATION: 'selling',
  SELLING: 'selling',
  BUYING: 'buying',
}

const Contact: React.FC = () => {
  const crumbs = useBreadcrumbs([{ label: 'Contact', path: '/contact' }])
  const [searchParams] = useSearchParams()
  const interestDefault = SUBJECT_TO_INTEREST[searchParams.get('subject') ?? '']
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    // TODO-HUMAN (P2.1): real endpoint — Cloudflare Worker + email provider.
    // Interim: honest mailto handoff, never a fake success alert.
    const data = new FormData(e.currentTarget)
    const subject = `Website inquiry — ${String(data.get('interest') || 'general')}`
    const body = [
      `Name: ${String(data.get('name') || '')}`,
      `Email: ${String(data.get('email') || '')}`,
      `Phone: ${String(data.get('phone') || '')}`,
      '',
      String(data.get('message') || ''),
    ].join('\n')
    trackEvent('contact_submit', { interest: String(data.get('interest') || 'general') })
    window.location.href = `mailto:shawn@jacklingo.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
  }

  return (
    <div>
      <Seo
        title="Contact a Lewes DE Real Estate Agent | Merritt & Shawn | Coastal Delaware Real Estate"
        description="Talk with Shawn McDonnell & Merritt Cooch — Lewes-based REALTORS® at Jack Lingo. Buying, selling, or a home valuation at the Delaware beaches starts here."
        path="/contact"
        jsonLd={[crumbs.jsonLd]}
      />
      {/* Hero Section */}
      <section className="relative h-[500px] flex items-center justify-center overflow-hidden">
        {/* Background image must be absolutely positioned — as a flex sibling it
            squeezed the hero copy into a squished column on mobile. */}
        <img
          src="/media/hero-poster.jpg"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover opacity-50"
        />
        <div className="absolute inset-0 bg-primary-navy/90 z-10"></div>
        <div className="relative z-20 text-center text-white px-6">
          <div className="flex justify-center">{crumbs.node}</div>
          <p className="inline-block font-display text-[12px] uppercase tracking-[0.3em] font-medium text-white/70 mb-4">
            Get In Touch
            <span className="block w-6 h-[2px] bg-accent-gold mt-3 mx-auto" aria-hidden="true" />
          </p>
          <h1 className="font-display uppercase leading-none tracking-tight text-[clamp(40px,6vw,84px)] mb-6">
            <span className="font-thin">Contact </span>
            <span className="font-bold text-accent-gold">Us</span>
          </h1>
          <p className="text-xl md:text-2xl text-white/90 max-w-2xl mx-auto">
            Ready to find your dream coastal home? Contact Shawn and Merritt today for a personalized consultation.
          </p>
        </div>
      </section>

      {/* Contact Information Section */}
      <Section className="bg-sand">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Contact Details */}
          <div className="lg:col-span-5 space-y-10">
            <div>
              <h2 className="font-display uppercase leading-none tracking-tight text-[clamp(32px,4vw,56px)] mb-6 text-primary-navy">
                <span className="font-thin">Get In </span>
                <span className="font-bold">Touch</span>
              </h2>
              <p className="text-lg md:text-xl text-neutral-slate leading-relaxed">
                Whether you're buying, selling, or just have questions about Delaware's coastal communities, we're here to help. Reach out today for expert guidance and personalized service.
              </p>
            </div>

            {/* Office Location */}
            <div className="space-y-4">
              <h3 className="text-2xl font-display uppercase tracking-wide font-bold text-primary-navy">Office Location</h3>
              <address className="not-italic text-neutral-slate space-y-2">
                <p className="text-lg">1240 Kings Highway</p>
                <p className="text-lg">Lewes, DE 19958</p>
              </address>
            </div>

            {/* Phone Contact */}
            <div className="space-y-4">
              <h3 className="text-2xl font-display uppercase tracking-wide font-bold text-primary-navy">Phone</h3>
              <div className="space-y-3 text-neutral-slate">
                <div className="flex items-center gap-3">
                  <span className="font-semibold text-primary-navy">Office:</span>
                  <a href="tel:302-645-2207" className="text-lg hover:text-primary-navy transition-colors">
                    302-645-2207
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-semibold text-primary-navy">Shawn Cell:</span>
                  <a href="tel:302-542-8591" className="text-lg hover:text-primary-navy transition-colors">
                    302-542-8591
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-semibold text-primary-navy">Merritt Cell:</span>
                  <a href="tel:302-562-7954" className="text-lg hover:text-primary-navy transition-colors">
                    302-562-7954
                  </a>
                </div>
              </div>
            </div>

            {/* Email Contact */}
            <div className="space-y-4">
              <h3 className="text-2xl font-display uppercase tracking-wide font-bold text-primary-navy">Email</h3>
              <div className="space-y-3 text-neutral-slate">
                <div className="flex items-center gap-3">
                  <span className="font-semibold text-primary-navy">Shawn:</span>
                  <a href="mailto:shawn@jacklingo.com" className="text-lg hover:text-primary-navy transition-colors">
                    shawn@jacklingo.com
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-semibold text-primary-navy">Merritt:</span>
                  <a href="mailto:merritt@jacklingo.com" className="text-lg hover:text-primary-navy transition-colors">
                    merritt@jacklingo.com
                  </a>
                </div>
              </div>
            </div>

            {/* Office Hours */}
            <div className="space-y-4 pt-6 border-t border-primary-navy/20">
              <h3 className="text-2xl font-display uppercase tracking-wide font-bold text-primary-navy">Availability</h3>
              {/* Office hours intentionally not listed — not verified (Part 4 order 4) */}
              <p className="text-neutral-slate">
                By appointment — call, text, or email and we'll find a time that works for you.
              </p>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-7">
            <div className="bg-white p-8 md:p-10 rounded-lg shadow-xl">
              <h2 className="font-display uppercase leading-none tracking-tight text-[clamp(28px,3.5vw,48px)] mb-8 text-primary-navy">
                <span className="font-thin">Send Us </span>
                <span className="font-bold">A Message</span>
              </h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-neutral-slate font-medium mb-2">
                    Your Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-5 py-4 rounded-lg bg-sand border-2 border-neutral-slate/30 focus:border-primary-navy focus:outline-none focus:ring-2 focus:ring-primary-navy/20 transition-all text-lg"
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-neutral-slate font-medium mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-5 py-4 rounded-lg bg-sand border-2 border-neutral-slate/30 focus:border-primary-navy focus:outline-none focus:ring-2 focus:ring-primary-navy/20 transition-all text-lg"
                    placeholder="john@example.com"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-neutral-slate font-medium mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    className="w-full px-5 py-4 rounded-lg bg-sand border-2 border-neutral-slate/30 focus:border-primary-navy focus:outline-none focus:ring-2 focus:ring-primary-navy/20 transition-all text-lg"
                    placeholder="(302) 555-1234"
                  />
                </div>
                <div>
                  <label htmlFor="interest" className="block text-neutral-slate font-medium mb-2">
                    Area of Interest
                  </label>
                  <select
                    id="interest"
                    name="interest"
                    key={interestDefault ?? 'default'}
                    defaultValue={interestDefault}
                    className="w-full px-5 py-4 rounded-lg bg-sand border-2 border-neutral-slate/30 focus:border-primary-navy focus:outline-none focus:ring-2 focus:ring-primary-navy/20 transition-all text-lg"
                  >
                    <option value="buying">Buying a Home</option>
                    <option value="selling">Selling a Home</option>
                    <option value="rental">Rental Property</option>
                    <option value="investment">Investment Property</option>
                    <option value="other">Other Inquiry</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="message" className="block text-neutral-slate font-medium mb-2">
                    Your Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={5}
                    required
                    className="w-full px-5 py-4 rounded-lg bg-sand border-2 border-neutral-slate/30 focus:border-primary-navy focus:outline-none focus:ring-2 focus:ring-primary-navy/20 transition-all text-lg resize-none"
                    placeholder="Tell us about your real estate needs..."
                  ></textarea>
                </div>
                <button
                  type="submit"
                  className="group w-full inline-flex items-center justify-center h-12 bg-accent-gold text-primary-ink font-display font-medium text-[12px] tracking-[0.15em] uppercase hover:bg-[#ca6f59] transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-accent-gold/50 focus:ring-offset-2"
                >
                  <span>Send Message</span>
                  <span className="ml-2 text-base leading-none transition-transform duration-300 group-hover:rotate-90" aria-hidden="true">
                    +
                  </span>
                </button>
                <p className="text-sm text-neutral-slate text-center pt-4">
                  This form opens your email app with the message pre-filled — or write us directly at{' '}
                  <a href="mailto:shawn@jacklingo.com" className="text-accent-gold hover:underline">shawn@jacklingo.com</a>.
                  Your information is kept confidential.
                </p>
              </form>
            </div>
          </div>
        </div>
      </Section>

      {/* Map Section */}
      <Section className="bg-neutral-charcoal">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-display uppercase leading-none tracking-tight text-[clamp(32px,4vw,56px)] mb-8 text-center text-white">
            <span className="font-thin">Visit Our </span>
            <span className="font-bold text-accent-gold">Office</span>
          </h2>
          <div className="h-[400px] md:h-[500px] rounded-lg overflow-hidden shadow-lg">
            <iframe
              title="Map — Jack Lingo REALTOR®, 1240 Kings Highway, Lewes, DE 19958"
              src="https://www.google.com/maps?q=1240+Kings+Highway,+Lewes,+DE+19958&output=embed"
              className="w-full h-full border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
          <div className="mt-6 text-center">
            <a
              href="https://maps.google.com/?q=1240+Kings+Highway,+Lewes,+DE"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-accent-gold hover:text-white transition-colors"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                />
              </svg>
              Open in Google Maps
            </a>
          </div>
        </div>
      </Section>

      {/* Licensed Footer Section */}
      <Section className="bg-neutral-charcoal border-t border-primary-navy/20">
        <div className="max-w-4xl mx-auto text-center">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {/* Brokerage Attribution */}
            <div className="space-y-3">
              <h3 className="text-xl font-display uppercase tracking-wide font-bold text-white">Merritt & Shawn | Coastal Delaware Real Estate</h3>
              <p className="text-sm text-white/70">
                Licensed in the State of Delaware
              </p>
              <p className="text-sm text-white/70">
                Jack Lingo, REALTOR®
              </p>
            </div>

            {/* Fair Housing & Equal Opportunity */}
            <div className="space-y-3">
              <h3 className="text-xl font-display uppercase tracking-wide font-bold text-white">Fair Housing</h3>
              <p className="text-sm text-white/70">
                We are dedicated to providing equal housing opportunities to all clients.
              </p>
              <div className="flex justify-center gap-4 mt-4">
                <div className="flex flex-col items-center gap-1">
                  <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center">
                    <span className="text-xs font-bold text-white/70">FH</span>
                  </div>
                  <span className="text-xs text-white/70">Fair Housing</span>
                </div>
                <div className="flex flex-col items-center gap-1">
                  <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center">
                    <span className="text-xs font-bold text-white/70">EO</span>
                  </div>
                  <span className="text-xs text-white/70">Equal Opportunity</span>
                </div>
              </div>
            </div>

            {/* Contact Summary */}
            <div className="space-y-3">
              <h3 className="text-xl font-display uppercase tracking-wide font-bold text-white">Contact Summary</h3>
              <p className="text-sm text-white/70">
                Office: 302-645-2207
              </p>
              <p className="text-sm text-white/70">
                Shawn Cell: 302-542-8591
              </p>
              <p className="text-sm text-white/70">
                Merritt Cell: 302-562-7954
              </p>
              <p className="text-sm text-white/70">
                Email: shawn@jacklingo.com
              </p>
            </div>
          </div>

          {/* Copyright */}
          <div className="border-t border-neutral-slate/30 pt-8">
            <p className="text-sm text-white/70">
              &copy; {new Date().getFullYear()} Merritt & Shawn | Coastal Delaware Real Estate. All rights reserved.
            </p>
            <p className="text-xs text-white/50 mt-2">
              Merritt & Shawn | Coastal Delaware Real Estate is a real estate team operating under Jack Lingo REALTOR®,
              a family-owned independent brokerage. Equal Housing Opportunity.
            </p>
          </div>
        </div>
      </Section>
    </div>
  )
}

export default Contact
